import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import InlineCropper from '../InlineCropper';
import BackgroundStyleSelector from './BackgroundStyleSelector';

function TryoutFields({
  formData,
  template,
  onChange,
  onAdditionalInfoChange,
  onDateFormatClick,
  teamColors,
  backgroundStyle,
  onBackgroundStyleChange,
  backgroundCropping,
  backgroundTempImage,
  backgroundPhotoPreview,
  onBackgroundPhotoChange,
  onBackgroundCropComplete,
  onBackgroundCropCancel,
  onEditBackgroundCrop,
  onRemoveBackgroundPhoto,
  onSetBackgroundPhotoFromUrl,
  saving,
  downloading,
}) {
  const showBackground = template === 'poster' || template === 'photoFrame';

  const [bgMode, setBgMode] = useState(() => {
    if (!backgroundPhotoPreview) return 'team-colors';
    if (backgroundPhotoPreview.includes('pexels.com')) return 'pexels';
    return 'upload';
  });
  const [pexelsQuery, setPexelsQuery] = useState('baseball tryouts');
  const [pexelsResults, setPexelsResults] = useState([]);
  const [pexelsLoading, setPexelsLoading] = useState(false);
  const [pexelsLoadingMore, setPexelsLoadingMore] = useState(false);
  const [pexelsPage, setPexelsPage] = useState(1);
  const [pexelsTotalResults, setPexelsTotalResults] = useState(0);

  useEffect(() => {
    if (!backgroundPhotoPreview) return;
    if (backgroundPhotoPreview.includes('pexels.com')) setBgMode('pexels');
    else setBgMode('upload');
  }, [backgroundPhotoPreview]);

  async function searchPexels(query, page = 1, append = false) {
    if (append) setPexelsLoadingMore(true);
    else setPexelsLoading(true);
    try {
      const res = await api.get('/pexels/search', { params: { query, page, per_page: 12 } });
      const photos = res.data.photos || [];
      setPexelsTotalResults(res.data.total_results || 0);
      setPexelsPage(page);
      if (append) {
        setPexelsResults((prev) => {
          const combined = [...prev, ...photos];
          return combined.filter((p, i, self) => i === self.findIndex((x) => x.id === p.id));
        });
      } else {
        setPexelsResults(photos);
      }
    } catch (err) {
      console.error('Pexels search failed:', err);
      if (!append) setPexelsResults([]);
    } finally {
      if (append) setPexelsLoadingMore(false);
      else setPexelsLoading(false);
    }
  }

  function handlePexelsSearch() {
    searchPexels(pexelsQuery, 1, false);
  }

  function handlePexelsLoadMore() {
    searchPexels(pexelsQuery, pexelsPage + 1, true);
  }

  return (
    <>
      <div className="form-group">
        <label htmlFor="heroFont">Tryouts Font</label>
        <select
          id="heroFont"
          name="heroFont"
          value={formData.heroFont || 'Bebas Neue'}
          onChange={onChange}
        >
          <option value="Bebas Neue">Bebas Neue</option>
          <option value="Oswald">Oswald</option>
          <option value="Russo One">Russo One</option>
          <option value="Black Ops One">Black Ops One</option>
          <option value="Teko">Teko</option>
          <option value="Orbitron">Orbitron</option>
          <option value="Barlow Condensed">Barlow Condensed</option>
        </select>
      </div>

      <div className="form-group">
        <label htmlFor="heroFontSize">
          Tryouts Text Size
          <span style={{ fontSize: '12px', color: '#718096', marginLeft: '8px' }}>
            ({formData.heroFontSize || 160}px)
          </span>
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '11px', color: '#718096', flexShrink: 0 }}>Smaller</span>
          <input
            id="heroFontSize"
            name="heroFontSize"
            type="range"
            min={40}
            max={600}
            step={10}
            value={formData.heroFontSize || 160}
            onChange={onChange}
            style={{ flex: 1 }}
          />
          <span style={{ fontSize: '11px', color: '#718096', flexShrink: 0 }}>Larger</span>
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="accentBlockColor">Accent Color</label>
        <input
          id="accentBlockColor"
          name="accentBlockColor"
          type="color"
          value={formData.accentBlockColor || teamColors?.primary || '#3b82f6'}
          onChange={onChange}
        />
      </div>

      <div className="form-group">
        <label htmlFor="detailTextColor">Detail Text Color</label>
        <input
          id="detailTextColor"
          name="detailTextColor"
          type="color"
          value={formData.detailTextColor || '#ffffff'}
          onChange={onChange}
        />
      </div>

      <div className="form-group">
        <label htmlFor="ageGroup">
          Age Group / Division *
          <span style={{ fontSize: '12px', color: '#718096', marginLeft: '8px' }}>
            ({formData.ageGroup.length}/30)
          </span>
        </label>
        <input
          id="ageGroup"
          name="ageGroup"
          type="text"
          value={formData.ageGroup}
          onChange={onChange}
          placeholder="Select age group"
          required
          maxLength={30}
        />
      </div>

      <div className="form-group">
        <label htmlFor="tryoutDate">Tryout Date *</label>
        <input
          id="tryoutDate"
          name="tryoutDate"
          type="date"
          value={formData.tryoutDate}
          onChange={onChange}
          placeholder="Select date"
          required
        />
        <button
          type="button"
          className="format-link"
          onClick={onDateFormatClick}
          style={{
            background: 'none',
            border: 'none',
            color: '#667eea',
            fontSize: '13px',
            cursor: 'pointer',
            marginTop: '4px',
            padding: '0',
            textDecoration: 'underline',
          }}
        >
          Change date format
        </button>
      </div>

      {['photoFrame', 'poster', 'infoCard'].includes(template) && (
      <div className="form-group">
        <label htmlFor="tryoutDate2">
          Second Tryout Date
          <span style={{ fontSize: '12px', color: '#718096', marginLeft: '8px' }}>(optional)</span>
        </label>
        <input
          id="tryoutDate2"
          name="tryoutDate2"
          type="date"
          value={formData.tryoutDate2 || ''}
          onChange={onChange}
          placeholder="Select second date"
        />
      </div>
      )}

      <div className="form-group">
        <label htmlFor="tryoutTime">Tryout Time *</label>
        <input
          id="tryoutTime"
          name="tryoutTime"
          type="time"
          value={formData.tryoutTime}
          onChange={onChange}
          placeholder="Select time"
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="location">
          Location
          <span style={{ fontSize: '12px', color: '#718096', marginLeft: '8px' }}>
            ({formData.location.length}/50)
          </span>
        </label>
        <input
          id="location"
          name="location"
          type="text"
          value={formData.location}
          onChange={onChange}
          placeholder="Enter location"
          maxLength={50}
        />
      </div>

      <div className="form-group">
        <label htmlFor="contact">
          Contact / Registration
          <span style={{ fontSize: '12px', color: '#718096', marginLeft: '8px' }}>
            ({formData.contact.length}/75)
          </span>
        </label>
        <input
          id="contact"
          name="contact"
          type="text"
          value={formData.contact}
          onChange={onChange}
          placeholder="Enter contact information"
          maxLength={75}
        />
      </div>

      <div className="form-group">
        <label htmlFor="additionalInfo">
          Additional Information
          <span style={{ fontSize: '12px', color: '#718096', marginLeft: '8px' }}>
            ({formData.additionalInfo.length}/300)
          </span>
        </label>
        <textarea
          id="additionalInfo"
          name="additionalInfo"
          value={formData.additionalInfo}
          onChange={onAdditionalInfoChange}
          placeholder="Tell players and parents about your team or organization..."
          rows="4"
          maxLength={300}
        />
      </div>

      {showBackground && (
        <>
          <BackgroundStyleSelector
            backgroundStyle={backgroundStyle}
            teamColors={teamColors}
            onBackgroundStyleChange={onBackgroundStyleChange}
          />

          <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '16px', marginTop: '8px' }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#4a5568', marginBottom: '12px', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
              Background
            </div>

            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
              <button
                type="button"
                className={bgMode === 'team-colors' ? 'btn-primary' : 'btn-secondary'}
                style={{ flex: 1, padding: '8px 4px', fontSize: '12px' }}
                onClick={() => { setBgMode('team-colors'); if (backgroundPhotoPreview) onRemoveBackgroundPhoto(); }}
              >
                Team Colors
              </button>
              <button
                type="button"
                className={bgMode === 'upload' ? 'btn-primary' : 'btn-secondary'}
                style={{ flex: 1, padding: '8px 4px', fontSize: '12px' }}
                onClick={() => setBgMode('upload')}
              >
                Upload Photo
              </button>
              <button
                type="button"
                className={bgMode === 'pexels' ? 'btn-primary' : 'btn-secondary'}
                style={{ flex: 1, padding: '8px 4px', fontSize: '12px' }}
                onClick={() => { setBgMode('pexels'); if (!pexelsResults.length) searchPexels('baseball tryouts'); }}
              >
                Browse Free
              </button>
            </div>

            {bgMode === 'upload' && (
              <div className="form-group">
                {backgroundCropping ? (
                  <InlineCropper
                    imageSrc={backgroundTempImage}
                    onCropComplete={onBackgroundCropComplete}
                    onCancel={onBackgroundCropCancel}
                  />
                ) : backgroundPhotoPreview && !backgroundPhotoPreview.includes('pexels.com') ? (
                  <div className="background-photo-preview">
                    <img src={backgroundPhotoPreview} alt="Background" />
                    <button type="button" className="btn-edit-photo-overlay" onClick={onEditBackgroundCrop} disabled={saving || downloading}>
                      Edit
                    </button>
                    <button type="button" className="btn-remove-background-photo" onClick={onRemoveBackgroundPhoto} disabled={saving || downloading}>
                      ✕ Remove
                    </button>
                  </div>
                ) : (
                  <label htmlFor="tryout-bg-photo" className="background-photo-upload-btn">
                    <span>Upload Background Photo</span>
                    <input
                      id="tryout-bg-photo"
                      type="file"
                      accept="image/jpeg,image/jpg,image/png"
                      onChange={onBackgroundPhotoChange}
                      style={{ display: 'none' }}
                      disabled={saving || downloading}
                    />
                  </label>
                )}
              </div>
            )}

            {bgMode === 'pexels' && (
              <div className="form-group">
                <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                  <input
                    type="text"
                    value={pexelsQuery}
                    onChange={(e) => setPexelsQuery(e.target.value)}
                    placeholder="baseball field, stadium…"
                    style={{ flex: 1 }}
                  />
                  <button type="button" onClick={handlePexelsSearch} className="btn-secondary" disabled={pexelsLoading} style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                    {pexelsLoading ? '…' : 'Search'}
                  </button>
                </div>

                {backgroundPhotoPreview?.includes('pexels.com') && (
                  <div className="brag-card-unsplash-active">
                    <img src={backgroundPhotoPreview} alt="Selected background" crossOrigin="anonymous" />
                    <button type="button" className="brag-card-remove-btn" onClick={onRemoveBackgroundPhoto}>
                      Remove
                    </button>
                  </div>
                )}

                {!pexelsLoading && pexelsResults.length === 0 && (
                  <p style={{ fontSize: '13px', color: '#718096', marginTop: '8px' }}>No photos found.</p>
                )}

                {pexelsResults.length > 0 && (
                  <div className="brag-card-unsplash-grid">
                    {pexelsResults.map((photo) => (
                      <button
                        key={photo.id}
                        type="button"
                        className={`brag-card-unsplash-thumb${backgroundPhotoPreview === photo.src.large ? ' selected' : ''}`}
                        onClick={() => onSetBackgroundPhotoFromUrl(photo.src.large)}
                        title={photo.photographer || ''}
                      >
                        <img src={photo.src.medium} alt={photo.alt || ''} crossOrigin="anonymous" />
                      </button>
                    ))}
                  </div>
                )}

                {pexelsResults.length > 0 && pexelsResults.length < pexelsTotalResults && (
                  <button
                    type="button"
                    className="brag-card-search-btn brag-card-load-more-btn"
                    onClick={handlePexelsLoadMore}
                    disabled={pexelsLoadingMore}
                    style={{ marginTop: '8px', width: '100%' }}
                  >
                    {pexelsLoadingMore ? 'Loading…' : 'Load more'}
                  </button>
                )}

                <p style={{ fontSize: '11px', color: '#718096', marginTop: '8px' }}>
                  Photos: <a href="https://www.pexels.com" target="_blank" rel="noreferrer">Pexels</a>
                </p>
              </div>
            )}

            {bgMode !== 'team-colors' && backgroundPhotoPreview && (
              <div style={{ marginTop: '12px' }}>
                <div style={{ fontSize: '12px', fontWeight: 600, color: '#4a5568', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Image Position &amp; Scale
                </div>
                <div className="form-group">
                  <label htmlFor="bgImageX">Left / Right</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '11px', color: '#718096', flexShrink: 0 }}>Left</span>
                    <input
                      id="bgImageX"
                      name="bgImageX"
                      type="range"
                      min={0}
                      max={100}
                      step={1}
                      value={formData.bgImageX ?? 50}
                      onChange={onChange}
                      style={{ flex: 1 }}
                    />
                    <span style={{ fontSize: '11px', color: '#718096', flexShrink: 0 }}>Right</span>
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="bgImageY">Up / Down</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '11px', color: '#718096', flexShrink: 0 }}>Up</span>
                    <input
                      id="bgImageY"
                      name="bgImageY"
                      type="range"
                      min={0}
                      max={100}
                      step={1}
                      value={formData.bgImageY ?? 50}
                      onChange={onChange}
                      style={{ flex: 1 }}
                    />
                    <span style={{ fontSize: '11px', color: '#718096', flexShrink: 0 }}>Down</span>
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="bgImageScale">
                    Zoom
                    <span style={{ fontSize: '12px', color: '#718096', marginLeft: '8px' }}>
                      ({formData.bgImageScale ?? 100}%)
                    </span>
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '11px', color: '#718096', flexShrink: 0 }}>Out</span>
                    <input
                      id="bgImageScale"
                      name="bgImageScale"
                      type="range"
                      min={10}
                      max={200}
                      step={5}
                      value={formData.bgImageScale ?? 100}
                      onChange={onChange}
                      style={{ flex: 1 }}
                    />
                    <span style={{ fontSize: '11px', color: '#718096', flexShrink: 0 }}>In</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
}

export default TryoutFields;
